import { NextPageWithLayout } from "../_app";
import { PropsWithChildren } from "react";
import Layout from "@/components/Layout/Layout";
import { getPage } from "@/data/page";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import Navigation from "@/modules/Navigation/Navigation.component";
import { getNavigation } from "@/data/navigation";
import MappedComponent from "@/modules/ComponentMap/ComponentMap";
import Footer, { FooterMeta } from "@/modules/Footer/Footer.component";
import Head from "next/head";
import MetaAttributes from "@/modules/MetaAttributes/MetaAttributes";
import Typo3PreviewBar from "@/modules/Typo3PreviewBar/Typo3PreviewBar";
import { getConfig } from "@/data/config";
import { Slot } from "utils/create-slots";
import { Config, PageProps } from "@/types/types";
import { PageContext } from "pages/[[...slug]]";
import ProductForm from "@/modules/Product/variants/ProductForm";
import Script from "next/script";

const Page: NextPageWithLayout<PropsWithChildren<PageProps>> = ({
  pageProps,
  navigation,
  footer,
  footerMeta,
  config,
  preview = false,
  editUrl,
}) => {
  if (!pageProps) return null;

  const {
    slug = "",
    breadcrumbs = [],
    content = {},
    meta = {},
    appearance = {},
  } = pageProps;

  // Safely access carData to prevent errors
  const carData = content.colPos1?.[0]?.content?.form?.car;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
      </Head>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
        strategy="afterInteractive"
      />
      <MetaAttributes meta={meta} slug={slug} />
      <PageContext.Provider value={{ config, breadcrumbs, meta, appearance }}>
        <Layout
          type={appearance.layout}
          transparentheader={appearance.transparentheader}
        >
          <Slot name="Navigation">
            <Navigation
              items={navigation}
              appearance={appearance}
              key={`nav-${appearance.transparentheader}`}
            />
          </Slot>
          <Slot name="Breadcrumbs">
            <Breadcrumbs items={breadcrumbs}>
              {breadcrumbs.map(({ link, title }) => (
                <Breadcrumbs.Item href={link} key={link}>
                  {title}
                </Breadcrumbs.Item>
              ))}
            </Breadcrumbs>
          </Slot>
          {Object.entries(content).map(([key, value]: any[]) => (
            <Slot name={key} key={key}>
              <div className="container">
                <div className="grid grid-cols-3">
                  <div className="w-full h-full lg:col-span-1 col-span-3">
                    <div className="container lg:py-16 py-8">
                      {carData && carData.internalid && (
                        <p className="mb-6 lg:mt-6 text-xs">
                          Fahrzeug-ID {carData.internalid}
                        </p>
                      )}
                      {carData && (
                        <ProductForm
                          priority={false}
                          item={carData}
                          mediaSizes={{
                            "(max-width: 1024px)": "25vw",
                            "(max-width: 1280px)": "33vw",
                            "": "25vw",
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="lg:col-span-2 col-span-3 lg:py-16 pt-0 pb-8">
                    <div className="container">
                      <h2 className="lg:mb-7 lg:text-5xl text-4xl">
                        Kaufanfrage
                      </h2>
                    </div>
                    {value.map((props, index) => (
                      <MappedComponent {...props} key={index} />
                    ))}
                  </div>
                </div>
              </div>
            </Slot>
          ))}
          <Slot name="Footer">
            <Footer items={footer} />
          </Slot>
          <Slot name="FooterMeta">
            <FooterMeta items={footerMeta} />
          </Slot>
        </Layout>
        {preview && <Typo3PreviewBar editUrl={editUrl} />}
      </PageContext.Provider>
    </>
  );
};

export async function getServerSideProps({
  req,
  preview = false,
  previewData,
}) {
  const pageProps: any = await getPage([req.url], preview, previewData);
  const { navigation, footer, footerMeta } = await getNavigation();
  const config: Config = await getConfig();

  console.log("BUILDING PAGE:: ", req.url);

  const carData = pageProps?.content?.colPos1?.[0]?.content?.form?.car;

  if (
    !carData || // Check for car data specifically
    !pageProps ||
    pageProps.status === 404 ||
    !navigation ||
    navigation.status === 404 ||
    !config ||
    config.status === 404
  ) {
    return { notFound: true };
  }

  if (pageProps.status >= 300 && pageProps.status <= 309) {
    return {
      redirect: {
        destination: `/${pageProps.url}`,
        permanent: true,
      },
    };
  }

  return {
    props: {
      pageProps,
      navigation,
      footer,
      footerMeta,
      config,
      preview,
      ...(preview
        ? {
            editUrl: `${process.env.NEXT_PUBLIC_API_URL}typo3/module/web/layout?id=${pageProps.id}`,
          }
        : {}),
    },
  };
}

export default Page;
